import { gql, useQuery } from '@apollo/client'

import {
  EntryCampaignRankingConnectionFragmentDoc,
  PortfoliosConnectionFragmentDoc,
  RankingBaseConnectionFragmentDoc,
  RankingPeriodBaseConnectionFragmentDoc,
  RankingStatus,
  RankingUserWithoutIdConnectionFragmentDoc,
  UserCollectionBoxFragmentDoc,
  UserCommonWithCustomProfileConnectionFragmentDoc,
  UserProfileQuery,
  UserProfileQueryVariables,
} from '@/clients/utoniq-core/schema'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router } = useRouter<{ userId: string }>()
  const { userId } = router.query

  const { data, loading } = useQuery<
    UserProfileQuery,
    UserProfileQueryVariables
  >(QUERY, {
    variables: {
      id: userId,
      userId,
      pageInputCollection: { limit: 10 },
    },
  })

  return {
    state: {
      loading,
      user: data?.user || null,
      userId,
      isCustom: !!data?.user?.customProfile,
      rankings: data?.organization?.entryCampaignRankings || null,
      items: data?.user?.organizationPortfolios || null,
      collections:
        data?.ownedUserCollections?.edges.filter(
          ({ node }) => node?.itemOwnedCount && node.itemOwnedCount >= 1
        ) ?? null,
      organizationArtistRankings:
        data?.organizationArtistRankings?.edges.filter(
          ({ node }) => node.status === RankingStatus.Active
        ) ?? null,
    },
    actions: {},
  }
})

const QUERY = gql`
  query UserProfile(
    $id: ID
    $userId: String
    $pageInput: PageInput
    $pageInputCollection: PageInput
  ) {
    user(id: $id) {
      ...UserCommonWithCustomProfileConnection
      organizationPortfolios(pageInput: $pageInput) {
        ...PortfoliosConnection
      }
    }
    organization {
      id
      entryCampaignRankings(id: $id) {
        ...EntryCampaignRankingConnection
      }
    }
    ownedUserCollections(userId: $userId, pageInput: $pageInputCollection) {
      edges {
        node {
          ...UserCollectionBox
        }
      }
    }
    organizationArtistRankings(userId: $userId) {
      edges {
        node {
          ... on Ranking {
            ...RankingBaseConnection
            latestRankingPeriod(periodType: monthly) {
              ...RankingPeriodBaseConnection
              myRanking(userId: $userId) {
                ...RankingUserWithoutIdConnection
              }
            }
          }
        }
      }
    }
  }

  ${UserCommonWithCustomProfileConnectionFragmentDoc}
  ${PortfoliosConnectionFragmentDoc}
  ${EntryCampaignRankingConnectionFragmentDoc}
  ${UserCollectionBoxFragmentDoc}
  ${RankingBaseConnectionFragmentDoc}
  ${RankingPeriodBaseConnectionFragmentDoc}
  ${RankingUserWithoutIdConnectionFragmentDoc}
`
